import type { FunctionComponent, HTMLAttributes, MouseEvent, ReactElement, ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useAudioTrigger } from '../../controllers/audioController/hooks';
import { useModifiers } from '../../styles/hooks/useModifiers';
import { IconButton } from '../controls/iconButton/IconButton';
import { Icon } from '../icons/icon';

import { mirrorVideo } from './mirrorVideo';

import styles from './BackToGame.module.css';

export interface BackToGameProps {
  /** Action label */
  actionLabel?: ReactNode;
  /** Whether the component is temporarily hidden */
  isHidden?: boolean;
  /** Whether the component is open and "alive" */
  isOpen: boolean;
  /** Game name */
  name?: ReactNode;
  /** On click handler */
  onClick?: HTMLAttributes<HTMLDivElement>['onClick'];
  /** On close handler */
  onClose?: HTMLAttributes<HTMLButtonElement>['onClick'];
  /** Placeholder image src */
  poster?: string;
  /** Traffic light ReactNode */
  trafficLight?: ReactElement;
  /** A video element to get video data from */
  videoElement?: HTMLVideoElement | null;
}

export const BackToGame: FunctionComponent<BackToGameProps> = ({
  actionLabel,
  isHidden,
  isOpen,
  name,
  onClick,
  onClose,
  poster,
  trafficLight,
  videoElement,
}) => {
  const audioTrigger = useAudioTrigger();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const backToGameRef = useRef<HTMLDivElement>(null);
  const [localIsOpen, setLocalIsOpen] = useState<boolean>(isOpen);

  const classNameWithMods = useModifiers(
    styles,
    'backToGame',
    {
      isOpen: isOpen && !isHidden,
    },
    { withBaseClass: true }
  );

  useEffect(() => {
    if (isOpen) {
      setLocalIsOpen(true);
    } else {
      if (backToGameRef.current) {
        const timeout = window.setTimeout(() => {
          setLocalIsOpen(false);
        }, 300);

        return () => {
          window.clearTimeout(timeout);
        };
      } else {
        setLocalIsOpen(false);
      }
    }
  }, [isOpen]);

  const handleClick = useCallback(
    (event: MouseEvent<HTMLDivElement>): void => {
      if (isOpen && onClick) {
        audioTrigger.play({ name: 'menuClick' });
        onClick(event);
      }
    },
    [audioTrigger, isOpen, onClick]
  );

  const handleCloseClick = useCallback(
    (event?: MouseEvent<HTMLButtonElement>): void => {
      if (isOpen && onClose && event) {
        event.stopPropagation();
        audioTrigger.play({ name: 'menuClick' });
        onClose(event);
      }
    },
    [audioTrigger, isOpen, onClose]
  );

  useEffect(() => {
    const canvasElement = canvasRef.current;
    if (!canvasElement || !videoElement || !localIsOpen) {
      return;
    }

    const draw = mirrorVideo(videoElement, canvasElement);
    if (!draw) {
      return;
    }
    let frameId: number;

    if ('requestVideoFrameCallback' in videoElement) {
      const loop = (): void => {
        draw();
        frameId = videoElement.requestVideoFrameCallback(loop);
      };

      loop();

      return () => {
        videoElement.cancelVideoFrameCallback(frameId);
      };
    } else {
      const loop = (): void => {
        draw();
        frameId = requestAnimationFrame(loop);
      };

      loop();

      return () => {
        cancelAnimationFrame(frameId);
      };
    }
  }, [videoElement, localIsOpen]);

  return (
    <div className={styles.backToGame__wrapper} data-testid="back-to-game">
      {localIsOpen ? (
        <div className={classNameWithMods} onClick={handleClick} ref={backToGameRef}>
          <div className={styles.backToGame__trafficLight}>{trafficLight}</div>
          <div className={styles.backToGame__container}>
            <div className={styles.backToGame__videoContainer}>
              <img src={poster} className={styles.backToGame__poster} alt={''} />
              <canvas className={styles.backToGame__video} width="384" height="216" ref={canvasRef} />
            </div>
            <div className={styles.backToGame__content}>
              <div className={styles.backToGame__name}>{name}</div>
              <div className={styles.backToGame__action}>
                {actionLabel}
                <Icon icon={'ChevronRight'} />
              </div>
            </div>
            {onClose ? (
              <div className={styles.backToGame__close}>
                <IconButton icon={'Close'} small squared ghost onClick={handleCloseClick} />
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};
